import { colors } from '@ardoq/design-tokens';
import {
  anchorNameForId,
  HTMLElementWithAnchor,
  Popover2025,
} from './Popover2025';
import styled from 'styled-components';
import { ReactNode, RefObject, useId } from 'react';
import { useAnchorDescribedBy } from './useAnchorDescribedBy';

export type TooltipProps = {
  isOpen: boolean;
  onClose: VoidFunction;
  anchorRef: RefObject<HTMLElementWithAnchor | null>;
  children: ReactNode;
};

const Arrow = styled.div<{ $anchorName: string }>`
  border: 6px solid transparent;
  pointer-events: none;
  position: fixed;
  position-anchor: ${({ $anchorName }) => $anchorName};
  left: anchor(center);
  top: anchor(bottom);
  /* Center the arrow box on the trigger's bottom edge so the full triangle
     sits in the gap and its apex points at the trigger. */
  transform: translate(-50%, -50%);
  border-bottom-color: ${colors.surfaceInverted};

  /* When the popover flips above the anchor, move the arrow to the top edge
     of the trigger and point it down. */
  @container anchored(fallback: flip-block) {
    top: auto;
    bottom: anchor(top);
    transform: translate(-50%, 50%);
    border-bottom-color: transparent;
    border-top-color: ${colors.surfaceInverted};
  }
`;

const StyledTooltip = styled.div`
  margin-top: 6px;
  margin-inline: 8px;
  position: relative;
  border-radius: 4px;
  background-color: ${colors.surfaceInverted};
  color: ${colors.textOnInvertedStrong};
  padding: 3px 8px;
  text-align: center;
  text-overflow: ellipsis;
  max-width: 280px;
  white-space: wrap;

  @container anchored(fallback: flip-block) {
    margin-top: 0;
    margin-bottom: 6px;
  }
`;

/**
 * Declarative Tooltip component. Use this when you
 * want to have full control over the tooltip's behavior.
 *
 * While open, the tooltip text is linked to the `anchorRef` element via
 * `aria-describedby` so assistive tech announces it when the anchor is focused.
 **/
export const Tooltip = (props: TooltipProps) => {
  const positioningId = useId();
  const contentId = useId();

  useAnchorDescribedBy({
    anchorRef: props.anchorRef,
    descriptionId: contentId,
    isActive: props.isOpen,
  });

  return (
    <Popover2025
      isOpen={props.isOpen}
      onClose={props.onClose}
      id={positioningId}
      anchorRef={props.anchorRef}
      placement={{ side: 'bottom', overflow: 'center' }}
    >
      <StyledTooltip role="tooltip" id={contentId}>
        <Arrow $anchorName={anchorNameForId(positioningId)} />
        {props.children}
      </StyledTooltip>
    </Popover2025>
  );
};
